/* eslint-disable no-unused-vars */
import React, { useState, useLayoutEffect, useRef, useEffect } from "react";
import styles from "./AskMyra.module.css";
import { useHistory } from "react-router-dom";
import checkBrowserSupport from "../self-assesment/browserDetect";
import { AnalyticsService, BrowserBlockScreen } from "../self-assesment/SelfAssesment";
import VoiceService from "../voice-agent/VH-Myra-FrontEnd";
import MyraChatService from "../voice-agent/MyraChatService";
import { useAssessment } from "../self-assesment/AssessmentContext";
import { LandingPageModal } from "../self-assesment/LandingPage";
import MyraInteractionController from "../self-assesment/MyraInteractionController";
import { SubPageHeader } from "./SubHeader";

// ============================================================
// CONSTANTS
// ============================================================
const LOGO_URL = "https://virdiodev.s3.us-east-2.amazonaws.com/sampleVideo/LogoPNG.png";

// ============================================================
// useScrollToTop
// ============================================================
const useScrollToTop = () => {
  useLayoutEffect(() => {
    window.scrollTo(0, 0);
  }, []);
};

// ============================================================
// FAQ data
// ============================================================
const FAQ_ITEMS = [
  {
    question: "What happens during the assessment?",
    answer: "The Guided Self-Assessment is a free evaluation that takes about 10 minutes. You will answer questions about your pain and do some simple movements. If your camera is enabled, I will use it to see how your body moves and what triggers discomfort. At the end, you get a Recovery Compass Score and a personalized recovery plan. You must be 18 or older to start.",
  },
  {
    question: "How long will this take?",
    answer: "The Guided Self-Assessment takes about 10 minutes, depending on what we find and how you move. After that, the full recovery plan is 8 weeks total. Week 1 is completely free with no credit card required, and if you decide to continue, the remaining 7 weeks are $68 paid up front.",
  },
  {
    question: "How is Myra choosing my plan?",
    answer: "That is a great question. I use everything from your assessment to build your plan. Your answers about your pain, where it happens, and what makes it better or worse all matter. If you enabled your camera, I also watched how you moved during the simple tests to see what patterns showed up. Then I picked exercises that address those specific patterns and matched them to the goals you selected. That is why your plan is different from someone else's plan.",
  },
  {
    question: "Do I need to turn on my camera?",
    answer: "Camera access is recommended, but not required. If you enable it, I can see how you move during the assessment and give you more personalized guidance. If you prefer not to use your camera, your answers alone are enough to build your plan. Either way, you are in control.",
  },
  {
    question: "How do you know these exercises won't make my pain worse?",
    answer: "That is exactly the right question to ask. I build your plan based on what I learn during the assessment, and I monitor how you respond to every exercise. Some discomfort during exercise is normal, like muscle fatigue or a stretch sensation that fades within a few minutes of stopping. But if you feel sharp, shooting pain, anything that travels down your arm or leg, or discomfort that lingers after you stop, that is your body telling you to back off, and we adjust. You are always in control, and your feedback after each session helps me make sure the plan stays right for you.",
  },
  {
    question: "Are you a real doctor, or are you just guessing based on what I type?",
    answer: "I am not a doctor, and I am not guessing. I am Myra, Virdio Health's recovery coach, powered by AI. I work with doctors of physical therapy and evidence-based research. What I do is use your answers and, if you enable your camera, how you move, to build a plan that is specific to you. But I am clear about what I am not—I cannot diagnose conditions, prescribe treatment, or replace medical care. The Guided Self-Assessment is an educational tool to help you understand your pain pattern and get personalized guidance. If something feels off or your symptoms worsen, a healthcare provider is the right person to see.",
  },
];

// ============================================================
// AskMyraPage — main export
// ============================================================
export const AskMyraPage = () => {
  useScrollToTop();
  const history = useHistory();
  const [openAccordion, setOpenAccordion] = useState(null);

  const [showMemberBlockScreen, setShowMemberBlockScreen] = useState(false);
  const { resetAssessmentData, assessmentData, updateAssessmentData, isMyRAIsActive, setIsMyRAIsActive } = useAssessment();
  const [isMicModalActive, setIsMicModalActive] = useState(false);
  const [micModalMsg, setMicModalMsg] = useState("To chat with Myra, please grant microphone access. Your audio is processed in real-time and never stored or recorded.");
  const [access, setAccess] = useState(true);
  const [isStartedLandingFlow, setIsStartedLandingFlow] = useState(false);
  const [isUserChatWithMyra, setIsUserChatWithMyra] = useState(false);
  const [turnNumber, setTurnNumber] = useState(0);

  // Chat A/B test state
  const [myraMode, setMyraMode] = useState(MyraInteractionController.getMode() || "unknown");
  const [chatMessages, setChatMessages] = useState([]);
  const [chatInput, setChatInput] = useState("");
  const [isChatLoading, setIsChatLoading] = useState(false);
  const [chatHistory, setChatHistory] = useState([]);
  const chatMessagesRef = useRef(null);

  const [userCancelMic, setUserCancelMic] = useState(false);

  // ---- Session initialization on page load ----
  useEffect(() => {
    const previousSessionId = window.trSessionId || localStorage.getItem("trSessionId");
    if (previousSessionId) {
      localStorage.removeItem("trSessionId");
      window.trSessionId = null;
    }

    const isRepeat = localStorage.getItem("isUserCameBackAgain") === "true";
    localStorage.setItem("isUserCameBackAgain", "true");

    const params = new URLSearchParams(window.location.search);
    const gclid = params.get("gclid");
    const urlVariant = params.get("variant");
    const resolvedVariant = urlVariant || localStorage.getItem("variant") || null;

    if (urlVariant) {
      localStorage.setItem("variant", urlVariant);
    }

    const isFromGoogle = !!(gclid || resolvedVariant);

    const trackingFields = {
      variant: resolvedVariant,
      isComeFromGoogle: isFromGoogle ? 1 : 0,
      isUserCameBackAgain: isRepeat ? 1 : 0,
      visitedAskMyra: 1,
    };

    AnalyticsService.startSession(trackingFields);
  }, []);

  const handleMyraClick = async () => {
    setIsStartedLandingFlow(true);
    setIsUserChatWithMyra(true);
    setMyraMode("voice");
    window.currentFlow = "flow_landing_page";
    VoiceService._currentFlow = "flow_landing_page";
    localStorage.removeItem("userCondition");
    window.MyraInteraction.switchMode("voice");

    if (window.mayraCode && window.mayraCode.handleHomeScreenMyraClick) {
      window.setIsMicModalActive = setIsMicModalActive;
      window.updateMyRAIsActive = setIsMyRAIsActive;

      if (window.preloadAllAudios) window.preloadAllAudios();
      if (window.videoUnlockPlay) window.videoUnlockPlay();
      if (window.mayraCode) window.mayraCode.setOnMicEnabled(null);

      window.mayraCode.setOnMicEnabled((enabledBefore) => {
        console.log("HomeScreen: Microphone enabled callback triggered, enabledBefore:", enabledBefore);
        window.mayraCode.setOnMicEnabled(null);

        const handleFlowStart = () => {
          window.mayraCode.setIsVisible(true);
          window.mayraCode.setIsFlowStarted(true);
          if (!window.trSessionId) {
            console.log("HomeScreen: No session ID found, starting new session.");
            AnalyticsService.startSession({ visitedAskMyra: 1 });
          }

          const checkAndStartFlow = () => {
            const isAudioReady = window.audioPlayer?.unlocked && window.audioPlayer?.ctx?.state === "running";
            if (isAudioReady) {
              window.mayraCode.startFlow("flow_landing_page");
            } else {
              console.log("HomeScreen: Audio not ready (ctx state:", window.audioPlayer?.ctx?.state, ")");
              if (window.audioPlayer?.ctx?.state === "suspended") {
                window.audioPlayer.initFromUserGesture();
              }
              setTimeout(checkAndStartFlow, 200);
            }
          };
          checkAndStartFlow();
        };
        setTimeout(handleFlowStart, 300);
      });

      await window.mayraCode.handleHomeScreenMyraClick(true);
    } else {
      console.log("handleHomeScreenMyraClick not available, using fallback");

      let micPermissionState = "prompt";
      try {
        if (navigator.permissions && navigator.permissions.query) {
          const status = await navigator.permissions.query({ name: "microphone" });
          micPermissionState = status.state;
        }
      } catch (e) {
        console.log("Permission query failed");
      }

      if (micPermissionState === "granted") {
        setIsMyRAIsActive(true);
        if (window.mayraCode) {
          window.mayraCode.setOnMicEnabled((enabledBefore) => {
            window.mayraCode.setOnMicEnabled(null);
            window.mayraCode.setIsVisible(true);
            if (window.preloadAllAudios) window.preloadAllAudios();
            if (window.videoUnlockPlay) window.videoUnlockPlay();
            if (!window.trSessionId) {
              console.log("HomeScreen: No session ID found, starting new session.");
              AnalyticsService.startSession({ visitedAskMyra: 1 });
            }
            const checkAndStart = () => {
              if (window.audioPlayer?.unlocked) {
                window.mayraCode.startFlow("flow_landing_page");
              } else {
                setTimeout(checkAndStart, 500);
              }
            };
            checkAndStart();
          });
          window.mayraCode.handleMicClick(true);
        }
      } else {
        setIsMicModalActive(!isMicModalActive);
        setIsMyRAIsActive(true);
        if (window.mayraCode) {
          window.setIsMicModalActive = setIsMicModalActive;
          window.updateMyRAIsActive = setIsMyRAIsActive;
          window.mayraCode.setOnMicEnabled((enabledBefore) => {
            window.mayraCode.setOnMicEnabled(null);
            window.mayraCode.setIsVisible(true);
            const checkAndStart = () => {
              if (!window.trSessionId) {
                console.log("HomeScreen: No session ID found, starting new session.");
                AnalyticsService.startSession({ visitedAskMyra: 1 });
              }
              if (window.audioPlayer?.unlocked) {
                window.mayraCode.startFlow("flow_landing_page");
              } else {
                setTimeout(checkAndStart, 500);
              }
            };
            checkAndStart();
          });
          window.mayraCode.handleMicClick();
        }
      }
    }
  };

  const handleStartAssessment = () => {
    resetAssessmentData();
    localStorage.removeItem("assessmentData");
    localStorage.removeItem("dataAfterInfo");
    if (!isUserChatWithMyra) {
      console.log("Didn't Started Landing Flow Removing Conditions");
      localStorage.removeItem("userCondition");
    }
    if (isUserChatWithMyra && window.MyraInteraction && window.MyraInteraction.getMode() !== "voice") {
      if (window.VoiceService) {
        window.VoiceService._releaseMic();
      }
    }

    try {
      if (window.gtag) {
        window.gtag("event", "conversion", { send_to: "AW-18002481001/4AZGCNCA7YUcEOmeoIhD" });
      }
    } catch (e) {
      console.warn(e);
    }

    window.screenTracker.exitScreen("landing", {
      exitReason: "started_gsa",
      completed: true,
    });

    const finalScreenFlow = window.screenTracker.getScreenFlow();
    const timingData = window.timeTracker.startGSATimer();
    let landingDurationSec = null;
    if (finalScreenFlow.length > 0) {
      const landingScreen = finalScreenFlow.find((s) => s.screen === "landing");
      if (landingScreen && landingScreen.enteredAt && landingScreen.exitedAt) {
        const start = new Date(landingScreen.enteredAt);
        const end = new Date(landingScreen.exitedAt);
        landingDurationSec = Math.round((end - start) / 1000);
      }
    }

    const updatedData = {
      ...assessmentData,
      started_assessment: true,
      myraEngagedLanding: isUserChatWithMyra ? true : false,
      myraInteractionMode: myraMode || "unknown",
      myraChatMessagesCount: chatMessages.filter((m) => m.role === "user").length,
      userCancelMic: userCancelMic,
      ...timingData,
      screenFlow: finalScreenFlow,
      gsaStartedAt: timingData,
      timeOnLandingSec: landingDurationSec,
      analyticsId: window.trSessionId || localStorage.getItem("trSessionId"),
      session_last_update: new Date().toISOString(),
      EngagedOnLanding: isStartedLandingFlow ? true : false,
    };

    updateAssessmentData(updatedData);
    localStorage.setItem("assessmentData", JSON.stringify(updatedData));
    AnalyticsService.trackStep(updatedData);

    if (!window.trSessionId) {
      AnalyticsService.startSession({ visitedAskMyra: 1 });
    }

    history.replace("/problem-area");
  };
  if (showMemberBlockScreen) {
    return <BrowserBlockScreen featureType="member" />;
  }

  const toggleAccordion = (index) => {
    setOpenAccordion(openAccordion === index ? null : index);
  };
  const onGrant = () => {
    setUserCancelMic(false);
    setIsMicModalActive(false);
    if (window.updateMicModalCancel) {
      window.updateMicModalCancel(true);
    }
    if (window._myraPermissionCallbacks?.onGrant) {
      window._myraPermissionCallbacks.onGrant();
    }
    const handleMicDenied = () => {
      if (window.MyraInteraction) {
        window.MyraInteraction.switchMode("chat");
        window.MyraInteraction.closeChat();
      }
      console.log("PERMISSION DENIED - Starting flow");
      if (window.mayraCode) {
        window.mayraCode.startFlow("flow_landing_page");
        window.mayraCode.handleMicClick(true, false, false, true);
      }
      document.removeEventListener("micPermissionDenied", handleMicDenied);
    };

    document.addEventListener("micPermissionDenied", handleMicDenied);
  };

  const oncancel = () => {
    setUserCancelMic(true);
    setIsStartedLandingFlow(false);
    setIsUserChatWithMyra(false);
    setAccess(false);
    setIsMyRAIsActive(false);
    if (window.updateMicModalCancel) {
      window.updateMicModalCancel();
    }
    setMicModalMsg("Mic access is needed for Myra to hear your questions. If you prefer not to allow mic access, you can select the Chat option next to Talk. Or, start the Guided Self-Assessment anytime without asking Myra questions.");
    if (window._myraPermissionCallbacks?.onCancel) {
      window._myraPermissionCallbacks.onCancel();
    }
  };
  const getCurrentMicState = async () => {
    if (typeof navigator !== "undefined" && navigator.permissions && navigator.permissions.query) {
      try {
        const status = await navigator.permissions.query({ name: "microphone" });
        return status.state; // 'granted' | 'denied' | 'prompt'
      } catch {
        return "prompt"; // fall back to our cached state
      }
    }
    return "prompt"; // Safari/older browsers
  };

  const handleChatSend = async () => {
    const text = chatInput.trim();
    if (!text || isChatLoading) return;
    const state = await getCurrentMicState();
    if (state === "granted") {
      window.VoiceService._releaseMic();
    }
    window.currentFlow = "flow_landing_page";
    VoiceService._currentFlow = "flow_landing_page";
    window.MyraInteraction.switchMode("chat");
    setMyraMode("chat");

    setChatMessages((prev) => [...prev, { role: "user", text }]);
    setChatInput("");
    setIsChatLoading(true);

    setTimeout(() => {
      if (chatMessagesRef.current) {
        chatMessagesRef.current.scrollTop = chatMessagesRef.current.scrollHeight;
      }
    }, 50);

    if (!isUserChatWithMyra) {
      setIsUserChatWithMyra(true);
      setIsStartedLandingFlow(true);
    }
    let screenName = "landing";
    const screenPayload = {};

    const sessionId = window.trSessionId || localStorage.getItem("trSessionId");
    const result = await MyraChatService.sendMessage(text, sessionId, chatHistory, turnNumber, screenPayload, screenName);
    if (result) {
      setTurnNumber(result.turnNumber);
    }

    console.log("Chat result:", result);
    const hasJsonBlock = /```json[\s\S]*?```/.test(result.response);

    if (hasJsonBlock) {
      result.response = `Sorry, I didn't quite catch that. Could you say that again?`;
    }

    setChatMessages((prev) => [...prev, { role: "myra", text: result.response }]);
    setChatHistory(result.conversationHistory);
    setIsChatLoading(false);

    setTimeout(() => {
      if (chatMessagesRef.current) {
        chatMessagesRef.current.scrollTop = chatMessagesRef.current.scrollHeight;
      }
    }, 50);
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter") handleChatSend();
  };

  const HandleFaqClicked = (answer) => {
    window.currentFlow = "flow_landing_page";
    VoiceService._currentFlow = "flow_landing_page";
    window.initFromUserGesture();
    if (window.audioPlayer && window.audioPlayer.pcm) {
      // Stoping the any ongoing PCM playback
      if (typeof window.audioPlayer.ttsPcmAbort === "function") {
        window.audioPlayer.ttsPcmAbort();
      }
      // Force reset state
      window.audioPlayer.pcm.active = false;
      window.audioPlayer.pcm.queuedSamples = 0;
      window.audioPlayer.pcm.endReceived = false;
      window.audioPlayer.pcm.floatQueue = [];
      window.audioPlayer.pcm.floatQueueSamples = 0;
      window.audioPlayer.pcm.sources.clear();
    }
    if (VoiceService._ws && VoiceService._ws.readyState === WebSocket.OPEN) {
      VoiceService._ws.send(JSON.stringify({ type: "say", text: answer }));
    } else {
      VoiceService.initialize({
        onConnected: () => {
          VoiceService._ws.send(JSON.stringify({ type: "say", text: answer }));
          console.log("Connected voice service...");
        },
      });
      const flowToStart = VoiceService._currentFlow || window.currentFlow;
      // VoiceService.startFlowWithoutAnnouncement(flowToStart);
      // window.mayraCode.handleMicClick(true, true, false, true);
      // No connection at all — store pending and reconnect
      // VoiceService._pendingExitAnnouncement = answer;
      VoiceService.startFlowWithoutAnnouncement(flowToStart, "", answer);
    }
    // VoiceService._ws = null;
    if (window.MyraInteraction && window.MyraInteraction.getMode() !== "voice") {
      VoiceService._releaseMic();
    }
  };

  return (
    <div className={styles.page}>
      {/* <SubPageHeader activePage="ask-myra" isStartedLandingFlow={isStartedLandingFlow} setShowMemberBlockScreen={setShowMemberBlockScreen} /> */}
      <SubPageHeader activePage="how-it-works" isStartedLandingFlow={true} setShowMemberBlockScreen={setShowMemberBlockScreen} />
      {isMicModalActive && <LandingPageModal message={micModalMsg} onGrant={onGrant} onCancel={oncancel} isMicModalActive={isMicModalActive} setIsMicModalActive={setIsMicModalActive} access={access} setAccess={setAccess} setMicModalMsg={setMicModalMsg} />}

      {/* ===== HERO ===== */}
      <section className={styles.heroSection}>
        <div className={styles.container}>
          <h1 className={styles.heroHeadline}>Have questions? Ask Myra.</h1>
          <p className={styles.heroSub}>Before you start your assessment, Myra can answer your questions about the process, your privacy, what to expect, and more.</p>

          {/* Open chat widget — input visible and ready on load */}
          <div className={styles.openChat}>
            <div className={styles.openChatInner}>
              <div className={styles.openChatBubble}>
                <div className={styles.myraAvatar}>
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
                  </svg>
                </div>
                <div className={styles.myraBubble}>
                  <strong>Hi, I'm Myra.</strong> What's on your mind? I'm happy to answer any questions about getting started.
                </div>
              </div>
              <div className={styles.myraChatMessages} ref={chatMessagesRef}>
                {chatMessages.map((msg, i) => (
                  <div key={i} className={msg.role === "user" ? styles.chatMsgUser : styles.chatMsgMyra}>
                    {msg.text}
                  </div>
                ))}
                {isChatLoading && (
                  <div className={styles.chatTypingIndicator}>
                    <span />
                    <span />
                    <span />
                  </div>
                )}
              </div>

              <div className={styles.chatInputRow}>
                <input type="text" className={styles.chatInput} placeholder="Type your question…" value={chatInput} onChange={(e) => setChatInput(e.target.value)} onKeyDown={handleKeyDown} autoFocus />
                <button className={styles.chatSendBtn} onClick={handleChatSend} aria-label="Send message">
                  <svg viewBox="0 0 24 24" fill="currentColor">
                    <path d="M2.01 21L23 12 2.01 3 2 10l15 2-15 2z" />
                  </svg>
                </button>
              </div>

              <div className={styles.chatSecondary}>
                {/* TODO (dev team): wire up voice mode via VoiceService */}
                {/* {DONE} */}
                <button className={styles.secondaryLink} onClick={handleMyraClick}>
                  Prefer to speak?
                </button>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ===== FAQ ACCORDION ===== */}
      <section className={styles.faqSection}>
        <div className={styles.container}>
          <div className={styles.sectionHeader}>
            <p className={styles.sectionLabel}>Have questions before you start?</p>
            <h2 className={styles.sectionTitle}>See answers to common questions before you start.</h2>
          </div>

          <div className={styles.accordionList}>
            {FAQ_ITEMS.map((item, index) => {
              const isOpen = openAccordion === index;
              return (
                <div key={index} className={`${styles.accordionItem} ${isOpen ? styles.accordionOpen : ""}`}>
                  <button className={styles.accordionTrigger} onClick={() => toggleAccordion(index)} aria-expanded={isOpen}>
                    <span className={styles.accordionQuestion}>{item.question}</span>
                    <svg className={`${styles.accordionChevron} ${isOpen ? styles.chevronOpen : ""}`} fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth="2">
                      <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
                    </svg>
                  </button>

                  {isOpen && (
                    <div className={styles.accordionBody}>
                      <div className={styles.accordionAnswer}>
                        <p>{item.answer}</p>
                      </div>
                      {/* TODO (dev team): wire up audio playback per FAQ item */}
                      <button className={styles.audioPlayPill} onClick={() => HandleFaqClicked(item.answer)}>
                        <svg fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth="2">
                          <path strokeLinecap="round" strokeLinejoin="round" d="M14.752 11.168l-3.197-2.132A1 1 0 0010 9.87v4.263a1 1 0 001.555.832l3.197-2.132a1 1 0 000-1.664z" />
                          <path strokeLinecap="round" strokeLinejoin="round" d="M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                        </svg>
                        Hear Myra's answer
                      </button>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* ===== FINAL CTA ===== */}
      <section className={styles.ctaSection}>
        <div className={styles.container}>
          <div className={styles.ctaCard}>
            <h2 className={styles.ctaHeading}>Ready to get started?</h2>
            <p className={styles.ctaSubtext}>Start your free Guided Self-Assessment and let Myra build your personalized plan.</p>
            <button className={styles.btnPrimary} onClick={handleStartAssessment}>
              Start My Free Assessment
            </button>
            <p className={styles.ctaReassurance}>No commitment. No credit card. Stop anytime.</p>
            <div className={styles.secondaryCtaRow}>
              <p className={styles.secondaryCtaLabel}>Want to learn more first?</p>
              <button className={styles.btnTextLink} onClick={() => history.push("/how-it-works")}>
                How the assessment works →
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* ===== DISCLAIMER ===== */}
      <div className={styles.disclaimer}>
        <div className={styles.container}>
          <p className={styles.disclaimerText}>StartFreePT provides educational guidance based on your input. It is not a substitute for medical advice, diagnosis, or treatment.</p>
        </div>
      </div>

      {/* ===== FOOTER ===== */}
      <footer className={styles.footer}>
        <div className={styles.container}>
          <p className={styles.footerText}>© 2025 Virdio Health. All rights reserved.</p>
        </div>
      </footer>
    </div>
  );
};
